import { PropertyPolicy } from '@aaa-ncnu-ie/mypolicy-api-policies';
import { Box, capitalize, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';

import ChevronDownIcon from '../../../../assets/icons/ChevronDown';
import ChevronUpIcon from '../../../../assets/icons/ChevronUp';
import ExpandableList from '../../../../components/ExpandableList';
import colors from '../../../../constants/colors';
import useAnalytics from '../../../../hooks/useAnalytics';

interface PropertyDetailsProps {
  data: PropertyPolicy;
  policyNumber?: string;
}

const PropertyDetails: React.FC<PropertyDetailsProps> = ({
  data: { propertyDetails },
  policyNumber,
}: PropertyDetailsProps) => {
  const { track } = useAnalytics();

  const PROPERTY_DETAILS = {
    burglarAlarm: 'Burglar Alarm',
    fireAlarm: 'Fire Alarm',
    mortgagee: 'Mortgage company',
  };
  const propertyMortgageeAddr = useMemo(() => {
    if (!propertyDetails?.mortgagee) {
      return [];
    }
    const {
      address: { addressLine1, addressLine2, city, stateProvCd, postalCode },
    } = propertyDetails.mortgagee;
    let addrLine1 = addressLine1;
    if (addressLine2) {
      addrLine1 += `, ${addressLine2}`;
    }
    const addrLine2 = `${capitalize(
      city.toLowerCase()
    )}, ${stateProvCd}, ${postalCode}`;
    return [addrLine1, addrLine2];
  }, [propertyDetails]);

  const content = (isExpanded: boolean) => (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        p: 2,
        width: 1
      }}>
      <Box sx={{ textAlign: 'left' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            fontSize: { mobile: 16, tablet: 18 },
            lineHeight: '24px',
            color: colors.csaaBlack,
            letterSpacing: { mobile: 0.3, tablet: 0.4 },
          }}
        >
          Property details
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: { mobile: 16, tablet: 18 },
            lineHeight: '24px',
            color: colors.csaaMid,
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            letterSpacing: { mobile: 0.3, tablet: 0.4 },
          }}
        >
          Year built: {propertyDetails?.yearBuilt}
        </Typography>
      </Box>
      <Stack
        sx={{
          background: colors.csaaDarkTransparent,
          cursor: 'pointer',
          p: 0.5,
          height: 'fit-content',
        }}
      >
        {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Stack>
    </Stack>
  );

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: colors.csaaBorder,
        borderRadius: 2,
      }}
    >
      <ExpandableList
        content={content}
        id="property-details"
        onClick={(isOpen) =>
          track('Property Details Details Toggled', {
            event_type: 'UX Modified',
            location: 'Feature Card',
            link: 'Property details',
            selection: isOpen ? 'Closed' : 'Opened',
            policy_number: policyNumber,
          })
        }
      >
        <Stack
          spacing={1}
          sx={{
            p: 2,
            backgroundColor: colors.csaaNeutralBackground,
            borderTop: '1px solid',
            borderColor: colors.csaaBorder,
            borderBottomRightRadius: 8,
            borderBottomLeftRadius: 8,
          }}
        >
          {Object.entries(PROPERTY_DETAILS)?.map(
            (item) =>
              ((item[0] === 'mortgagee' && propertyDetails?.mortgagee) ||
                item[0] !== 'mortgagee') && (
                <Box
                  key={`${item[0]}-detail-item`}
                  sx={{
                    backgroundColor: colors.csaaWhite,
                    p: { mobile: 2, tablet: 3 },
                    boxShadow: `inset 0px 0px 0px 1px ${colors.csaaNeutral}`,
                    borderRadius: 2,
                  }}
                >
                  <Stack direction="row" sx={{
                    justifyContent: "space-between"
                  }}>
                    <Typography
                      sx={{
                        fontFamily: 'Cabin',
                        fontKerning: 'normal',
                        fontSize: { mobile: 16, tablet: 18 },
                        lineHeight: '24px',
                        fontWeight: 700,
                        letterSpacing: { mobile: 0.3, tablet: 0.4 },
                      }}
                    >
                      {item[1]}
                    </Typography>
                    {(item[0] === 'burglarAlarm' ||
                      item[0] === 'fireAlarm') && (
                      <Typography
                        sx={{
                          fontFamily: 'Cabin',
                          fontKerning: 'normal',
                          fontSize: { mobile: 16, tablet: 18 },
                          lineHeight: '24px',
                          fontWeight: 700,
                          letterSpacing: { mobile: 0.3, tablet: 0.4 },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {propertyDetails?.[item[0]]}
                      </Typography>
                    )}
                  </Stack>
                  {item[0] === 'mortgagee' && propertyDetails.mortgagee && (
                    <>
                      <Typography
                        sx={{
                          fontFamily: 'Cabin',
                          fontKerning: 'normal',
                          fontWeight: 400,
                          fontSize: { mobile: 16, tablet: 18 },
                          lineHeight: '24px',
                          color: colors.csaaMid,
                          letterSpacing: { mobile: 0.3, tablet: 0.4 },
                          mt: 1,
                        }}
                      >
                        {propertyDetails.mortgagee?.bankName}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Cabin',
                          fontKerning: 'normal',
                          fontWeight: 400,
                          fontSize: { mobile: 16, tablet: 18 },
                          lineHeight: '24px',
                          color: colors.csaaMid,
                          letterSpacing: { mobile: 0.3, tablet: 0.4 },
                        }}
                      >
                        {propertyMortgageeAddr[0]}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Cabin',
                          fontKerning: 'normal',
                          fontWeight: 400,
                          fontSize: { mobile: 16, tablet: 18 },
                          lineHeight: '24px',
                          color: colors.csaaMid,
                          letterSpacing: { mobile: 0.3, tablet: 0.4 },
                        }}
                      >
                        {propertyMortgageeAddr[1]}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Cabin',
                          fontKerning: 'normal',
                          fontWeight: 400,
                          fontSize: { mobile: 16, tablet: 18 },
                          lineHeight: '24px',
                          color: colors.csaaMid,
                          letterSpacing: { mobile: 0.3, tablet: 0.4 },
                        }}
                      >
                        Loan number: {propertyDetails.mortgagee?.loanNumber}
                      </Typography>
                    </>
                  )}
                </Box>
              )
          )}
        </Stack>
      </ExpandableList>
    </Box>
  );
};

export default PropertyDetails;
